<script setup>
    import { inject, computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { getPrettyPrice, getCashPrice, getCardPrice } from '../shared/utils';
    import useCartStore from '../store/cart';

    let rootData = inject('rootData');

    const props = defineProps({ hideCash: {
        type: Boolean,
        default: false,
    } });

    const cartStore = useCartStore();
    const { t } = useI18n();

    // Delivery row: render the fee only when it is actually known (flat fee or a
    // live Uber quote); otherwise show a soft, non-numeric note so the customer
    // knows delivery exists and the price is coming — see cart.deliveryDisplay.
    const delivery = computed(() => cartStore.deliveryDisplay);
    // Copy comes straight from the store descriptor (single source of truth),
    // so this row and the address field never drift in wording.
    const deliveryNote = computed(() => delivery.value.note);
    const showDeliveryFee = computed(
        () => delivery.value.state === 'flat' || delivery.value.state === 'quoted'
    );
    const showDeliveryRow = computed(() => delivery.value.state !== 'none');
    const deliveryEtaLabel = computed(
        () => (delivery.value.state === 'quoted' && delivery.value.eta)
            ? t('deliveryField.etaMin', { eta: delivery.value.eta })
            : ''
    );
</script>

<template>
    <div class="cart-total" v-if="cartStore.count > 0">
        <dl>
            <dt>{{ t('total.subtotal') }}</dt>
            <dd v-text="getPrettyPrice(cartStore.total)"></dd>
            <template v-if="showDeliveryRow">
                <dt>
                    {{ t('total.deliveryCharge') }}
                    <span v-if="deliveryEtaLabel" class="delivery-eta" v-text="deliveryEtaLabel"></span>
                </dt>
                <dd v-if="showDeliveryFee" v-text="getPrettyPrice(delivery.amountCents)"></dd>
                <dd v-else class="delivery-note" :class="{ 'delivery-note--warn': delivery.state === 'no_coverage' || delivery.state === 'error' || delivery.state === 'blocked' }" v-text="deliveryNote"></dd>
            </template>
            <dt>{{ t('total.estimatedTax') }}</dt>
            <dd v-text="getPrettyPrice(cartStore.taxes)"></dd>
            <template v-if="rootData.dualpricing">
                <dt class="totalRow">{{ t('total.totalCard') }}</dt>
                <dd class="totalRow" v-text="getPrettyPrice( getCardPrice( cartStore.checkoutTotal ) )"></dd>
                <dt class="totalRow">{{ t('total.totalCash') }}</dt>
                <dd class="totalRow" v-text="getPrettyPrice( getCashPrice(cartStore.checkoutTotal) )"></dd>
            </template>
            <template v-else>
                <dt class="totalRow">{{ t('total.total') }}</dt>
                <dd class="totalRow" v-text="getPrettyPrice(cartStore.checkoutTotal)"></dd>
            </template>
        </dl>
    </div>
</template>

<style lang="scss">
.cart-total {
    font-size: 0.875rem;

    dl {
        padding: 1rem;
        margin: 0;
        border-top: 1px solid var(--text-secondary);
        display: grid;
        grid-template-columns: repeat(12, 1fr);
        gap: 0.25rem 0.5rem; 
    }
    dt, dd {
        // margin: 0 0 .375em;
        &:last-of-type {
            margin-bottom: 0;
        }
    }
    dt {
        grid-column: span 8;
    }
    dd {
        grid-column: span 4;
        text-align: right;
    }

    .totalRow {
        font-weight: 600;
    }

    .delivery-eta {
        font-weight: 400;
        opacity: 0.65;
        margin-left: 0.375rem;
        font-size: 0.8125em;
    }
    .delivery-note {
        font-weight: 400;
        opacity: 0.7;
        font-size: 0.8125em;
        font-style: italic;
    }
    .delivery-note--warn {
        color: #b00020;
        opacity: 1;
        font-style: normal;
    }
}
</style>